import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

// Drop a pending wrap if the main add never settles (failed add, app error).
const PENDING_TIMEOUT = 10000;

/**
 * Opt-in gift wrapping checkbox rendered above the PDP buy buttons (blocks/gift-wrap.liquid).
 *
 * Every PDP add path goes through the product form: the ATC button click, or
 * `requestSubmit()` from the sticky bar and variant modal. MonsterCart stops the
 * ATC click below `document` and never lets the form submit, so the intent is
 * recorded from document-level capture listeners for both `click` and `submit`.
 * Once the main item has settled in the cart, the gift wrap variant is added in
 * a second request: through MonsterCart's API when the app is loaded (it owns
 * the drawer state), otherwise through /cart/add with a theme CartAddEvent so
 * the native drawer re-renders.
 *
 * @typedef {object} GiftWrapOptionRefs
 * @property {HTMLInputElement} checkbox - The opt-in checkbox.
 *
 * @extends {Component<GiftWrapOptionRefs>}
 */
class GiftWrapOption extends Component {
  requiredRefs = ['checkbox'];

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {number} quantity of wraps waiting for the main add to settle */
  #pendingQuantity = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pendingTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId ?? ''));
    document.addEventListener('click', this.#handleClick, { capture: true });
    document.addEventListener('submit', this.#handleSubmit, { capture: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleThemeCartUpdate);
    document.addEventListener('cart:updated', this.#handleMonsterCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('click', this.#handleClick, { capture: true });
    document.removeEventListener('submit', this.#handleSubmit, { capture: true });
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleThemeCartUpdate);
    document.removeEventListener('cart:updated', this.#handleMonsterCartUpdate);
    this.#clearPending();
  }

  get #isMonsterCart() {
    return typeof (/** @type {any} */ (window).monster_addToCart) === 'function';
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[type="submit"]') : null;
    if (button instanceof HTMLButtonElement && !button.disabled && button.form === this.#form) {
      this.#recordIntent();
    }
  };

  /** @param {Event} event */
  #handleSubmit = (event) => {
    if (event.target === this.#form) this.#recordIntent();
  };

  #recordIntent() {
    if (!this.#form || !this.refs.checkbox.checked) return;

    const quantityInput = /** @type {HTMLInputElement | null} */ (this.#form.querySelector('[name="quantity"]'));
    this.#pendingQuantity = Number(quantityInput?.value) || 1;

    clearTimeout(this.#pendingTimeout);
    this.#pendingTimeout = setTimeout(() => this.#clearPending(), PENDING_TIMEOUT);
  }

  /** @param {Event} event */
  #handleThemeCartUpdate = (event) => {
    if (!this.#pendingQuantity || this.#isMonsterCart) return;

    const { data } = /** @type {CartAddEvent} */ (event).detail;
    if (data?.source !== 'product-form-component') return;
    if (data.didError) {
      this.#clearPending();
      return;
    }

    this.#addWithTheme(this.#takePending());
  };

  #handleMonsterCartUpdate = () => {
    if (!this.#pendingQuantity || !this.#isMonsterCart) return;

    // Taken before the call so the cart:updated fired by the wrap add itself is ignored.
    const quantity = this.#takePending();
    /** @type {any} */ (window).monster_addToCart({ id: Number(this.dataset.variantId), quantity }, true);
    this.#reset();
  };

  /** @param {number} quantity */
  async #addWithTheme(quantity) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(this.dataset.variantId), quantity }],
            sections: sectionIds.join(','),
          }),
        })
      );
      const cart = await response.json();
      if (cart.status) return;

      this.dispatchEvent(
        new CartAddEvent({}, this.dataset.variantId, {
          source: 'gift-wrap-option',
          itemCount: quantity,
          variantId: this.dataset.variantId,
          sections: cart.sections,
        })
      );
      this.#reset();
    } catch (error) {
      console.error(error);
    }
  }

  /** @returns {number} */
  #takePending() {
    const quantity = this.#pendingQuantity;
    this.#clearPending();
    return quantity;
  }

  #clearPending() {
    this.#pendingQuantity = 0;
    clearTimeout(this.#pendingTimeout);
  }

  // The wrap is in the cart now; untick so a second add doesn't silently add another one.
  #reset() {
    this.refs.checkbox.checked = false;
  }
}

if (!customElements.get('gift-wrap-option')) {
  customElements.define('gift-wrap-option', GiftWrapOption);
}
